import type { IntlShape } from 'react-intl-next';

import { detectDateCadence } from './detectDateCadence.ts';
import {
	getParentKeywords,
	getRecurrenceKeywords,
	getStatusUpdateKeywords,
	getTitleKeywords,
} from './getKeywords.ts';
import { matchesAnyKeyword } from './matchesAnyKeyword.ts';
import { matchesStatusUpdateTemplate } from './matchesStatusUpdateTemplate.ts';
import { parseTitleDate } from './parseTitleDate.ts';

/** A child page/folder of the current content, as known by the caller. */
export type StatusUpdateChild = {
	title: string;
	createdAt: string | number | Date;
};

export type StatusUpdateSignalInput = {
	title?: string;
	parentTitle: string | null;
	templateId: string | null;
	children?: readonly StatusUpdateChild[];
};

export type StatusUpdateSignalMatch = {
	matches: boolean;
	matchedTitle: boolean;
	matchedChildren: boolean;
	matchedTemplate: boolean;
	matchedParentTitle: boolean;
};

/**
 * A recurring status-update title pairs a recurrence indicator (a cadence word,
 * "weekly", "monthly", "quarterly", "yearly") with an update word ("update",
 * "status", "recap", "report") — e.g. "Aug update", "weekly report".
 */
const isRecurringUpdateTitle = (title: string | undefined, intl: IntlShape): boolean =>
	matchesAnyKeyword(title, getRecurrenceKeywords(intl)) &&
	matchesAnyKeyword(title, getStatusUpdateKeywords(intl));

/**
 * Signals a recurring status-update series from a set of children.
 * A child only counts when its title uses an update word ("update", "status",
 * "recap", "report")
 */
const doesChildrenLookLikeSeries = (
	children: readonly StatusUpdateChild[] | undefined,
	intl: IntlShape,
): boolean => {
	if (!children || children.length === 0) {
		return false;
	}

	// Only consider children with an update-related keyword in their title.
	const statusUpdateRelatedKeywords = getStatusUpdateKeywords(intl);
	const childrenWithUpdateRelatedKeyword = children.filter((child) =>
		matchesAnyKeyword(child.title, statusUpdateRelatedKeywords),
	);
	if (childrenWithUpdateRelatedKeyword.length === 0) {
		return false;
	}

	const titleDates: number[] = [];
	const creationDates: (string | number | Date)[] = [];
	for (const child of childrenWithUpdateRelatedKeyword) {
		creationDates.push(child.createdAt);
		const titleDate = parseTitleDate(child.title, intl);
		if (titleDate !== undefined) {
			titleDates.push(titleDate);
		}
	}

	return Boolean(detectDateCadence(creationDates)) || Boolean(detectDateCadence(titleDates));
};

/**
 * Returns each signal that makes the page look like a status update.
 */
export const getStatusUpdateSignalMatch = (
	input: StatusUpdateSignalInput,
	intl: IntlShape,
): StatusUpdateSignalMatch => {
	const { title, parentTitle } = input;

	const matchedTitle =
		matchesAnyKeyword(title, getTitleKeywords(intl)) || isRecurringUpdateTitle(title, intl);
	const matchedChildren = doesChildrenLookLikeSeries(input.children, intl);
	const matchedTemplate = matchesStatusUpdateTemplate(input.templateId);
	const matchedParentTitle = matchesAnyKeyword(parentTitle, getParentKeywords(intl));

	return {
		matches: matchedTitle || matchedChildren || matchedTemplate || matchedParentTitle,
		matchedTitle,
		matchedChildren,
		matchedTemplate,
		matchedParentTitle,
	};
};
